import { Context, Controller } from '@hotwired/stimulus';
import { emitter } from 'src/emitter';
import { toggleAttribute } from 'src/utils/dom-toggle';

type AuditFieldInputEventParams = {
  id: string | null;
  name: string;
  value: string;
  count: number;
};

type FilePreviewEvent = {
  element: HTMLElement;
  file?: {
    name: string;
    size: number;
    type: string;
  };
};

let _supportsTemplates: null | boolean = null;
function supportsTemplates() {
  if (_supportsTemplates !== null) {
    return _supportsTemplates;
  }

  return (_supportsTemplates = 'content' in document.createElement('template'));
}

/**
 * Enables attaching a comment / message to checklist questions.
 */
export default class ChecklistsAttachmentController extends Controller {
  static targets = ['template', 'hidden', 'button'];

  private declare templateTarget: HTMLTemplateElement;
  private declare hiddenTarget: HTMLInputElement;
  private declare buttonTarget: HTMLButtonElement;

  private declare hasTemplateTarget: boolean;
  private declare hasHiddenTarget: boolean;
  private declare hasButtonTarget: boolean;

  constructor(context: Context) {
    super(context);

    this.onAddMessage = this.onAddMessage.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onFileChanged = this.onFileChanged.bind(this);
    this.onKeyboardShortcut = this.onKeyboardShortcut.bind(this);
  }

  connect(): void {
    super.connect();

    if (!(this.hasButtonTarget && supportsTemplates())) {
      return;
    }

    const fieldset = (
      this.hasButtonTarget ? this.buttonTarget : this.element
    ).closest('fieldset,[data-commentable-boundary]');
    const input = fieldset?.querySelector<
      HTMLInputElement | HTMLTextAreaElement
    >('input:not([type="hidden"]):not([type="submit"]),textarea');
    const currentValue = fieldset?.querySelector(
      'input[checked],input:checked,input[type="checkbox"]',
    );

    if (!fieldset || currentValue) {
      if (this.hasButtonTarget) {
        this.buttonTarget.removeAttribute('disabled');
      }
    } else if (input) {
      switch (input.type) {
        case 'radio': {
          fieldset.addEventListener('change', this.onEnable, { once: true });
          break;
        }
        case 'file': {
          emitter.on('file-preview:changed', this.onFileChanged);
          emitter.on('file-preview:cleared', this.onFileChanged);
          emitter.on('file-direct-upload:uploaded', this.onFileChanged);
          break;
        }
        default: {
          fieldset.addEventListener('input', this.onInput);
        }
      }

      emitter.on('audit-field:input', this.onKeyboardShortcut);
    }
  }

  disconnect(): void {
    if (this.hasButtonTarget) {
      this.buttonTarget.setAttribute('disabled', '');
    }

    const fieldset = (
      this.hasButtonTarget ? this.buttonTarget : this.element
    ).closest('fieldset,[data-commentable-boundary]');

    if (fieldset) {
      const input = fieldset?.querySelector<
        HTMLInputElement | HTMLTextAreaElement
      >('input:not([type="hidden"]):not([type="submit"]),textarea');

      if (input) {
        switch (input.type) {
          case 'radio': {
            fieldset.removeEventListener('change', this.onEnable);
            break;
          }
          case 'file': {
            emitter.off('file-preview:changed', this.onFileChanged);
            emitter.off('file-preview:cleared', this.onFileChanged);
            emitter.off('file-direct-upload:uploaded', this.onFileChanged);
            break;
          }
          default: {
            fieldset.removeEventListener('input', this.onInput);
          }
        }

        emitter.off('audit-field:input', this.onKeyboardShortcut);
      }
    }
  }

  private onEnable(event: Event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio'
    ) {
      const fieldset = event.currentTarget as HTMLFieldSetElement;
      const button = fieldset.querySelector(
        '[data-checklists--attachment-target="button"]:disabled',
      );

      toggleAttribute('disabled', button, false);
    }
  }

  private onInput(event: Event) {
    if (
      event.target instanceof HTMLInputElement &&
      event.target.type === 'radio'
    ) {
      return;
    }

    if (
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement
    ) {
      const fieldset = event.currentTarget as HTMLFieldSetElement;
      const button = fieldset.querySelector(
        '[data-checklists--attachment-target="button"]',
      );

      toggleAttribute(
        'disabled',
        button,
        !event.target.value && event.target.type !== 'checkbox',
      );
    }
  }

  private onFileChanged(params: unknown) {
    if (!params || typeof params !== 'object') {
      return;
    }

    if (!this.hasButtonTarget) {
      return;
    }

    const { element } = params as FilePreviewEvent;

    const fieldset = this.buttonTarget.closest(
      'fieldset,[data-commentable-boundary]',
    );

    if (!fieldset?.contains(element)) {
      return;
    }

    const button = fieldset.querySelector(
      '[data-checklists--attachment-target="button"]',
    );

    toggleAttribute('disabled', button, !('file' in params));
  }

  private onKeyboardShortcut(params: unknown) {
    if (!params || typeof params !== 'object') {
      return;
    }

    if (!Object.prototype.hasOwnProperty.call(params, 'id')) {
      return;
    }

    if (!this.hasButtonTarget) {
      return;
    }

    const { name, value } = params as AuditFieldInputEventParams;

    const fieldset = this.buttonTarget.closest(
      'fieldset,[data-commentable-boundary]',
    );
    const selected = fieldset?.querySelector<HTMLInputElement>(
      'input[checked],input:checked',
    );

    if (
      fieldset &&
      selected &&
      selected.name === name &&
      selected.value === value
    ) {
      const button = fieldset.querySelector(
        '[data-checklists--attachment-target="button"]:disabled',
      );
      button?.removeAttribute('disabled');
    }
  }

  private onAddMessage(event: Event) {
    if (!this.hasTemplateTarget) {
      return;
    }

    if (!('content' in document.createElement('template'))) {
      alert('Your browser does not support this feature.');
      return;
    }

    if (this.hasHiddenTarget) {
      this.hiddenTarget.remove();
    }

    const inflated =
      this.templateTarget.content.firstElementChild!.cloneNode(true);

    this.templateTarget.replaceWith(...Array.from(inflated.childNodes));

    if (event.currentTarget instanceof HTMLElement) {
      event.currentTarget.remove();
    }
  }
}
